/**
 * Entity Data Point Registry
 *
 * Canonical registry of data points for entity-style agreements
 * (Template.kind === 'CONTRACT' with contractType in NDA / DPA / MSA / SLA,
 *  plus future EMPLOYMENT / VENDOR types).
 *
 * Surfaces:
 * - Designer ContextRail filters this list by `category` so admins can drag
 *   typed canonical keys into PDF / structured fields.
 * - Envelope Generate-time resolution — the entity-agreement-context builder
 *   maps these keys to live values from Tenant / Entity-Framework before
 *   sending to DocuSeal.
 *
 * Multi-tenancy: this registry is platform-scoped (no businessId). A tenant
 * can override per-template field bindings, but the canonical-key vocabulary
 * is shared.
 *
 * Sensitive-key invariant (CLAUDE.md §2 + #1531 v4 §6.2): entity agreements
 * (NDA/MSA/SLA/DPA) are keyed off business identity, not personal PII —
 * `ssn`/`dob`/`credit_score` MUST NOT appear in this list. EMPLOYMENT-style
 * contracts that do need SSN must opt in via getSensitiveKeysForContractType
 * in get-data-points-for-kind.ts. The test suite enforces this.
 *
 * @see #1531 v4 §5 + §6.6 — per-kind canonical-key registries
 */

export interface EntityDataPoint {
  /** Unique canonical key used across ContextRail, designer field binding, and envelope generation */
  canonicalKey: string;
  /** Human-readable display name shown in the designer's data-point picker */
  label: string;
  /** Tooltip / help text describing what the value represents */
  description: string;
  /** Logical grouping for ContextRail accordion sections */
  category: 'entity' | 'parties' | 'agreement';
  /** Expected value type for runtime coercion at envelope-Generate time */
  type: 'string' | 'number' | 'date' | 'boolean';
  /** Whether the field MUST be resolved before the envelope can be sent */
  required: boolean;
}

export const ENTITY_DATA_POINTS: readonly EntityDataPoint[] = [
  // --------------------------------------------------------------------------
  // ENTITY (the business)
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'entity_legal_name',
    label: 'Entity Legal Name',
    description: 'Legal name of the contracting entity',
    category: 'entity',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'entity_business_address',
    label: 'Entity Business Address',
    description: 'Registered address',
    category: 'entity',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'entity_ein',
    label: 'Entity EIN',
    description: 'Federal Employer Identification Number',
    category: 'entity',
    type: 'string',
    required: false,
  },
  {
    canonicalKey: 'entity_state_of_formation',
    label: 'State of Formation',
    description: 'US state where the entity was formed',
    category: 'entity',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'entity_type',
    label: 'Entity Type',
    description: 'LLC / Corporation / LP / etc.',
    category: 'entity',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // PARTIES
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'counterparty_name',
    label: 'Counterparty Name',
    description: 'Name of the other party',
    category: 'parties',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'counterparty_email',
    label: 'Counterparty Email',
    description: 'Primary contact email',
    category: 'parties',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'authorized_signer_name',
    label: 'Authorized Signer Name',
    description: 'Person who can bind the entity',
    category: 'parties',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'authorized_signer_title',
    label: 'Authorized Signer Title',
    description: 'Title of the authorized signer',
    category: 'parties',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // AGREEMENT terms
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'agreement_subject',
    label: 'Agreement Subject',
    description: 'What the agreement covers (e.g., "MSA for SaaS services")',
    category: 'agreement',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'effective_date',
    label: 'Effective Date',
    description: 'When the agreement takes effect',
    category: 'agreement',
    type: 'date',
    required: true,
  },
  {
    canonicalKey: 'duration_years',
    label: 'Duration (years)',
    description: 'Initial term length in years',
    category: 'agreement',
    type: 'number',
    required: false,
  },
];

/** Flat list of every canonical key in this registry — convenience for callers that just need string lookup. */
export const ENTITY_CANONICAL_KEYS: readonly string[] = ENTITY_DATA_POINTS.map((p) => p.canonicalKey);
